import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { 
  Loader2, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Stethoscope,
  Mail,
  User,
  Building,
  MapPin,
  CreditCard,
  Calendar,
  Lock,
  ArrowRight
} from 'lucide-react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { verifyInvitation, acceptInvitation } from '@/lib/services/invitation';
import type { InvitationData } from '@/lib/services/invitation';
import { useTranslation } from 'react-i18next';

const createAcceptInviteSchema = (t: (key: string) => string) => z.object({
  password: z.string()
    .min(8, t('auth.acceptInvite.validation.passwordLength'))
    .regex(/[A-Z]/, t('auth.acceptInvite.validation.passwordUppercase'))
    .regex(/[a-z]/, t('auth.acceptInvite.validation.passwordLowercase'))
    .regex(/[0-9]/, t('auth.acceptInvite.validation.passwordNumber')),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: t('auth.acceptInvite.validation.passwordsMismatch'),
  path: ['confirmPassword'],
});

type AcceptInviteFormValues = z.infer<ReturnType<typeof createAcceptInviteSchema>>

export default function AcceptInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('doctor-platform');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  const token = searchParams.get('token');
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AcceptInviteFormValues>({
    resolver: zodResolver(createAcceptInviteSchema(t)),
  });

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError(t('auth.acceptInvite.errors.invalidLink'));
        setLoading(false);
        return;
      }

      try {
        const result = await verifyInvitation(token);
        
        if (!result.valid) {
          setError(result.error || t('auth.acceptInvite.errors.invalidInvitation'));
        } else if (result.invitation) {
          setInvitation(result.invitation);
          setIsExpired(result.isExpired || false);
          setIsAccepted(result.isAccepted || false);
        }
      } catch (err) {
        setError(t('auth.acceptInvite.errors.verifyFailed'));
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token, t]);

  const onSubmit = async (values: AcceptInviteFormValues) => {
    if (!invitation || !token) return;
    
    setSubmitting(true);
    setSubmitError(null);
    
    try {
      const result = await acceptInvitation({
        token,
        password: values.password,
        fullName: invitation.doctor_name, // Use invitation name
        specialization: [], // Optional, can be added later
        licenseExpiry: undefined // Optional, can be added later
      });

      if (result.success) {
        setSuccessMessage(t('auth.acceptInvite.success.created'));
        // Redirect to login with email pre-filled after a short delay
        setTimeout(() => {
          navigate('/auth/login', { 
            state: { 
              email: invitation.email,
              message: t('auth.acceptInvite.success.loginRedirectMessage')
            } 
          });
        }, 2000);
      } else {
        throw new Error(result.error || t('auth.acceptInvite.errors.createFailed'));
      }
    } catch (err: any) {
      setSubmitError(err.message || t('auth.acceptInvite.errors.createFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">{t('auth.acceptInvite.verifying')}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <CardTitle>{t('auth.acceptInvite.invalidTitle')}</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardFooter className="flex justify-center">
            <Button onClick={() => navigate('/auth/login')}>
              {t('auth.acceptInvite.goToLogin')}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (isAccepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-yellow-600" />
            </div>
            <CardTitle>{t('auth.acceptInvite.acceptedTitle')}</CardTitle>
            <CardDescription>
              {t('auth.acceptInvite.acceptedDescription')}
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex justify-center">
            <Button onClick={() => navigate('/auth/login')}>
              {t('auth.acceptInvite.goToLogin')}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <CardTitle>{t('auth.acceptInvite.expiredTitle')}</CardTitle>
            <CardDescription>
              {t('auth.acceptInvite.expiredDescription', {
                date: invitation ? new Date(invitation.expires_at).toLocaleDateString() : 'N/A'
              })}
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex justify-center">
            <Button onClick={() => navigate('/auth/login')}>
              {t('auth.acceptInvite.goToLogin')}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
            <Stethoscope className="h-8 w-8 text-green-600" />
          </div>
          <CardTitle className="text-2xl">{t('auth.acceptInvite.welcomeTitle')}</CardTitle>
          <CardDescription>
            {t('auth.acceptInvite.welcomeDescription')}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Invitation Details */}
          {invitation && (
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                {t('auth.acceptInvite.invitationDetails')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {invitation.doctor_name && (
                  <div className="flex items-start gap-2">
                    <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-muted-foreground">{t('auth.acceptInvite.fields.name')}</p>
                      <p className="font-medium">{invitation.doctor_name}</p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-start gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-muted-foreground">{t('auth.acceptInvite.fields.email')}</p>
                    <p className="font-medium">{invitation.email}</p>
                  </div>
                </div>
                
                {invitation.medical_license && (
                  <div className="flex items-start gap-2">
                    <CreditCard className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-muted-foreground">{t('auth.acceptInvite.fields.medicalLicense')}</p>
                      <p className="font-medium">{invitation.medical_license}</p>
                    </div>
                  </div>
                )}
                
                {invitation.practice_name && (
                  <div className="flex items-start gap-2">
                    <Building className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-muted-foreground">{t('auth.acceptInvite.fields.practice')}</p>
                      <p className="font-medium">{invitation.practice_name}</p>
                    </div>
                  </div>
                )}
                
                {invitation.country && (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-muted-foreground">{t('auth.acceptInvite.fields.country')}</p>
                      <p className="font-medium">{invitation.country}</p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-start gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-muted-foreground">{t('auth.acceptInvite.fields.expires')}</p>
                    <p className="font-medium">{new Date(invitation.expires_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Separator />

          {/* Success Message */}
          {successMessage && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                {successMessage}
              </AlertDescription>
            </Alert>
          )}

          {/* Error Message */}
          {submitError && (
            <Alert className="border-red-200 bg-red-50">
              <XCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {submitError}
              </AlertDescription>
            </Alert>
          )}

          {/* Password Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.acceptInvite.createPassword')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  {...register('password')}
                  id="password"
                  type="password"
                  placeholder={t('auth.acceptInvite.createPasswordPlaceholder')}
                  className="pl-10"
                  disabled={submitting}
                />
              </div>
              {errors.password && (
                <p className="text-sm text-red-600">{errors.password.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                {t('auth.acceptInvite.passwordHint')}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('auth.acceptInvite.confirmPassword')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  {...register('confirmPassword')}
                  id="confirmPassword"
                  type="password"
                  placeholder={t('auth.acceptInvite.confirmPasswordPlaceholder')}
                  className="pl-10"
                  disabled={submitting}
                />
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t('auth.acceptInvite.approvalNotice')}
              </AlertDescription>
            </Alert>

            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
              size="lg"
            >
              {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t('auth.acceptInvite.creatingAccount')}
                  </>
                ) : (
                  <>
                    {t('auth.acceptInvite.completeRegistration')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
